/**
 * Meta Pixel utility for Facebook/Meta tracking
 * Fires e-commerce events via the Meta Pixel (loaded via GTM)
 * Uses event IDs for deduplication with server-side CAPI
 * 
 */

import { STORES_API, OPTIONS } from '../data/Data';

// Track fired events to prevent duplicates within session
const firedPurchaseEvents = new Set();
const firedCheckoutEvents = new Set();
const firedPageViewUrls = new Set();
/**
 * Check if Meta Pixel is available in the browser
 */
const isPixelAvailable = () => typeof window !== 'undefined' && typeof window.fbq === 'function';

/**
 * Track Purchase event via Meta Pixel
 * Uses transactionId as event_id for deduplication with CAPI
 * Returns true if event was fired, false if duplicate or pixel not available
 */
export const trackPurchase = data => {
  if (firedPurchaseEvents.has(data.transactionId) || !isPixelAvailable()) {
    return false;
  }
  window.fbq('track', 'Purchase', {
    value: data.value,
    currency: data.currency,
    content_name: data.contentName,
    content_type: 'product',
    contents: [{
      id: 'IDP',
      quantity: 1
    }]
  }, {
    eventID: data.transactionId
  });
  firedPurchaseEvents.add(data.transactionId);
  return true;
};

/**
 * Track InitiateCheckout event via Meta Pixel
 * Returns true if event was fired, false if duplicate or pixel not available
 */
export const trackInitiateCheckout = data => {
  if (firedCheckoutEvents.has(data.checkoutId) || !isPixelAvailable()) {
    return false;
  }
  window.fbq('track', 'InitiateCheckout', {
    value: data.value,
    currency: data.currency,
    content_type: 'product',
    contents: [{
      id: 'IDP',
      quantity: 1
    }]
  }, {
    eventID: data.checkoutId
  });
  firedCheckoutEvents.add(data.checkoutId);
  return true;
};

/**
 * Reset state (useful for testing)
 */
export const resetPixelState = () => {
  firedPurchaseEvents.clear();
  firedCheckoutEvents.clear();
  firedPageViewUrls.clear();
};
/**
 * Get Meta cookie value by name
 */
const getCookie = name => {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};

/**
 * Get Meta Pixel cookies for CAPI deduplication
 * - fbp: Facebook Browser ID (_fbp cookie)
 * - fbc: Facebook Click ID (_fbc cookie, present if user came from FB ad)
 */
export const getMetaCookies = () => {
  return {
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc')
  };
};

/**
 * Track PageView event via Meta Pixel (client-side)
 * No event_id needed for PageView events
 * Returns true if event was fired, false if duplicate or pixel not available
 */
export const trackPageView = () => {
  if (!isPixelAvailable()) {
    return false;
  }
  const url = window.location.href;
  if (firedPageViewUrls.has(url)) {
    return false;
  }
  window.fbq('track', 'PageView');
  firedPageViewUrls.add(url);
  return true;
};

/**
 * Send PageView event to server-side CAPI
 * Includes fbp and fbc for matching
 */
export const sendServerPageView = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const {
    fbp,
    fbc
  } = getMetaCookies();
  fetch(`${STORES_API}/meta/page-view`, {
    ...OPTIONS,
    body: JSON.stringify({
      url: window.location.href,
      fbp,
      fbc
    })
  }).catch(() => {
    // Silently fail - PageView tracking should not disrupt user experience
  });
};

/**
 * Track PageView on both client-side pixel and server-side CAPI
 * Call this on route changes
 */
export const trackPageViewWithCapi = () => {
  trackPageView();
  sendServerPageView();
};